import React from 'react';
import type { HeroCapabilityId } from '../types';

const svgProps = {
  className: 'hero-capability-icon',
  width: 28,
  height: 28,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

/** Inline icons for the hero capability strip (no icon dependency). */
const HeroCapabilityIcon: React.FC<{ id: HeroCapabilityId }> = ({ id }) => {
  switch (id) {
    case 'software':
      return (
        <svg {...svgProps}>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
          <path d="m10 8-2 2 2 2M14 8l2 2-2 2" />
        </svg>
      );
    case 'embedded':
      return (
        <svg {...svgProps}>
          <rect x="7" y="7" width="10" height="10" rx="1" />
          <path d="M7 11H5M7 13H5M19 11h-2M19 13h-2M11 7V5M13 7V5M11 19v-2M13 19v-2" />
          <rect x="10" y="10" width="4" height="4" rx="0.5" />
        </svg>
      );
    case 'hardware':
      return (
        <svg
          className="hero-capability-icon"
          width={28}
          height={28}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden
        >
          <path d="M14.5 1h-13l-.5.5v13l.5.5h13l.5-.5v-13l-.5-.5zM14 14H5v-2h2.3c.3.6 1 1 1.7 1 1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2H4v3H2V2h2v2.3c-.6.3-1 1-1 1.7 0 1.1.9 2 2 2s2-.9 2-2h2c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2c-.7 0-1.4.4-1.7 1H6.7c-.3-.6-1-1-1.7-1V2h9v12zm-6-3c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1-1-.4-1-1zM5 5c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm6 0c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1z" />
        </svg>
      );
    case 'reverseEngineering':
      return (
        <svg
          className="hero-capability-icon"
          width={28}
          height={28}
          viewBox="0 0 490 490"
          fill="none"
          stroke="currentColor"
          strokeWidth={36}
          strokeLinecap="round"
          aria-hidden
        >
          <path d="m280,278a153,153 0 1,0-2,2l170,170m-91-117 110,110-26,26-110-110" />
        </svg>
      );
    case 'aiAssisted':
      return (
        <svg
          className="hero-capability-icon"
          width={28}
          height={28}
          viewBox="0 0 512 512"
          fill="currentColor"
          aria-hidden
        >
          <g transform="translate(64, 64)" fillRule="evenodd">
            <path d="M320,64 L320,320 L64,320 L64,64 L320,64 Z M171.749388,128 L146.817842,128 L99.4840387,256 L121.976629,256 L130.913039,230.977 L187.575039,230.977 L196.319607,256 L220.167172,256 L171.749388,128 Z M260.093778,128 L237.691519,128 L237.691519,256 L260.093778,256 L260.093778,128 Z M159.094727,149.47526 L181.409039,213.333 L137.135039,213.333 L159.094727,149.47526 Z M341.333333,256 L384,256 L384,298.666667 L341.333333,298.666667 L341.333333,256 Z M85.3333333,341.333333 L128,341.333333 L128,384 L85.3333333,384 L85.3333333,341.333333 Z M170.666667,341.333333 L213.333333,341.333333 L213.333333,384 L170.666667,384 L170.666667,341.333333 Z M85.3333333,0 L128,0 L128,42.6666667 L85.3333333,42.6666667 L85.3333333,0 Z M256,341.333333 L298.666667,341.333333 L298.666667,384 L256,384 L256,341.333333 Z M170.666667,0 L213.333333,0 L213.333333,42.6666667 L170.666667,42.6666667 L170.666667,0 Z M256,0 L298.666667,0 L298.666667,42.6666667 L256,42.6666667 L256,0 Z M341.333333,170.666667 L384,170.666667 L384,213.333333 L341.333333,213.333333 L341.333333,170.666667 Z M0,256 L42.6666667,256 L42.6666667,298.666667 L0,298.666667 L0,256 Z M341.333333,85.3333333 L384,85.3333333 L384,128 L341.333333,128 L341.333333,85.3333333 Z M0,170.666667 L42.6666667,170.666667 L42.6666667,213.333333 L0,213.333333 L0,170.666667 Z M0,85.3333333 L42.6666667,85.3333333 L42.6666667,128 L0,128 L0,85.3333333 Z" />
          </g>
        </svg>
      );
    case 'industrialization':
      return (
        <svg {...svgProps}>
          <path d="M3 21h18M5 21V12l3-1.5V21M11 21V9l3-1.5V21M17 21V6l3 1.5V21" />
          <path d="M17 6V4a2 2 0 012-2h0a2 2 0 012 2v2" />
          <path d="M3 21V10l4-2v13" />
        </svg>
      );
    default:
      return null;
  }
};

export default HeroCapabilityIcon;
